import { apiClient } from './api';
import type { UserRole } from '@/types/models';

export interface UserSummary {
  id: string;
  keycloakSub?: string;
  email: string;
  username: string;
  role: UserRole;
  organizationId: string | null;
  /** Resolved display name for organizationId (from the org mirror); null for users with no org. */
  organizationName: string | null;
  isActive: boolean;
  fullName?: string;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface OrganizationInvitation {
  id: string;
  email: string;
  role: UserRole;
  status: string;
  createdAt: string;
  expiresAt: string;
  token?: string;
}

export interface UserRoleChangeResult {
  id: string;
  role: UserRole;
}

interface PaginatedUsersResponse {
  data: UserSummary[];
  total: number;
  page: number;
  pageSize: number;
  /** Active/inactive/distinctRoles counts across ALL users (not just the current page). */
  statusCounts?: { active: number; inactive: number; distinctRoles: number };
}

interface InviteUserPayload {
  email: string;
  role: UserRole;
  /** Platform admins only — targets a specific org instead of their own. */
  organizationId?: string;
}

function toUserSummary(row: any): UserSummary {
  return {
    id: String(row.id ?? row.keycloakSub ?? ''),
    keycloakSub: row.keycloakSub ? String(row.keycloakSub) : undefined,
    email: String(row.email ?? ''),
    username: String(row.username ?? row.email ?? ''),
    role: (row.role ?? '') as UserRole,
    organizationId: row.organizationId ? String(row.organizationId) : null,
    organizationName: row.organizationName ?? row.organization_name ?? null,
    isActive: Boolean(row.isActive ?? row.is_active ?? true),
    fullName: row.fullName ?? row.full_name ?? undefined,
    createdAt: String(row.createdAt ?? row.created_at ?? new Date().toISOString()),
    lastLoginAt: row.lastLoginAt ?? row.last_login ?? null,
  };
}

function toInvitation(row: any): OrganizationInvitation {
  const rawToken = row.token ?? row.inviteToken;
  return {
    id: String(row.id ?? ''),
    email: String(row.email ?? ''),
    role: (row.role ?? '') as UserRole,
    status: String(row.status ?? 'pending'),
    createdAt: String(row.createdAt ?? row.created_at ?? new Date().toISOString()),
    expiresAt: String(row.expiresAt ?? row.expires_at ?? new Date().toISOString()),
    token: rawToken ? String(rawToken) : undefined,
  };
}

export const usersService = {
  /** `organizationId` is only honored for platform admins (see UsersService.listOrganizationUsers server-side) — everyone else always gets their own org's roster regardless of what's passed. */
  async listOrgUsers(organizationId?: string): Promise<UserSummary[]> {
    const response = await apiClient.get<any[]>('/ums/users/org-members', {
      params: organizationId ? { organizationId } : undefined,
    });
    return (Array.isArray(response) ? response : []).map(toUserSummary);
  },

  async listAll(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    role?: string;
    status?: string;
  }): Promise<PaginatedUsersResponse> {
    const hasParams =
      params?.page ||
      params?.pageSize ||
      params?.search ||
      (params?.role && params.role !== 'all') ||
      (params?.status && params.status !== 'all');
    const response = await apiClient.get<PaginatedUsersResponse | any[]>('/ums/users', {
      params: hasParams ? {
        page: params?.page,
        pageSize: params?.pageSize,
        search: params?.search,
        role: params?.role && params.role !== 'all' ? params.role : undefined,
        status: params?.status && params.status !== 'all' ? params.status : undefined,
      } : undefined,
    });

    if (Array.isArray(response)) {
      const data = response.map(toUserSummary);
      return {
        data,
        total: data.length,
        page: 1,
        pageSize: data.length,
      };
    }

    const rawRows = Array.isArray(response?.data) ? response.data : [];
    return {
      data: rawRows.map(toUserSummary),
      total: Number(response?.total ?? rawRows.length),
      page: Number(response?.page ?? 1),
      pageSize: Number(response?.pageSize ?? rawRows.length),
      statusCounts: response?.statusCounts,
    };
  },

  async listInvitations(): Promise<OrganizationInvitation[]> {
    return [];
  },

  async invite(data: InviteUserPayload): Promise<OrganizationInvitation> {
    const response = await apiClient.post<any>('/ums/users/org-members/invite', data);
    return toInvitation(response);
  },

  /** Create a user directly, without an email invitation. */
  async create(data: {
    email: string;
    username?: string;
    displayName: string;
    role: string;
    password: string;
    contactPhone?: string;
    /** Platform admins only — targets a specific org instead of their own. */
    organizationId?: string;
  }): Promise<{ id: string; email: string; role: string }> {
    return apiClient.post('/ums/users/org-members', data);
  },

  async changeRole(id: string, data: { role: UserRole }): Promise<UserRoleChangeResult> {
    const response = await apiClient.patch<any>(`/ums/users/org-members/${id}/role`, data);
    return {
      id: String(response.id ?? id),
      role: (response.role ?? data.role) as UserRole,
    };
  },

  async revokeInvitation(_id: string) {
    return { success: false };
  },

  async removeMember(id: string) {
    await apiClient.delete(`/ums/users/org-members/${id}`);
    return { success: true };
  },

  async setMemberStatus(id: string, enabled: boolean) {
    return apiClient.patch<{ id: string; isActive: boolean }>(`/ums/users/org-members/${id}/status`, { enabled });
  },

  async updateMemberRole(id: string, role: UserRole) {
    return this.changeRole(id, { role });
  },

  /**
   * Uploads a profile picture and returns its URL. Saving it onto the account
   * is a separate updateMe() call, matching how org logos work.
   */
  async uploadAvatar(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ url: string }>('/ums/media/avatar', formData);
    return response.url;
  },

  /** Uploads a profile cover banner and returns its URL. */
  async uploadBanner(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ url: string }>('/ums/media/banner', formData);
    return response.url;
  },

  async updateMe(data: { displayName?: string; avatarUrl?: string; bannerUrl?: string }) {
    return apiClient.patch<{
      displayName: string | null;
      avatarUrl: string | null;
      bannerUrl: string | null;
    }>('/ums/users/me', data);
  },
};
